import React, { useMemo, useState, useCallback } from "react";
import { Card, Button, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { FaPlus, FaEye } from "react-icons/fa";
import CustomTableWithFrontend from "../../../components/customTableWithFrontend/CustomTableWithFrontend";
import TableSearchPanel from "../../../components/tableSearchPanel/TableSearchPanel";
import { InvoiceTableView, InvoiceStatusView } from "../../../app/models/api/invoice/invoiceTableView";
import { mapInvoiceResponseToTable, invoiceStatusColorMapper } from "./invoiceStatusMappers";
import { namespaces } from "../../../i18n/i18n.constants";
import { routes } from "../../../app/routes/routes";
import { ColumnType } from "../../../app/models/components/columnType";
import UploadInvoiceModal from "../../modals/invoices/uploadInvoice/UploadInvoiceModal";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import { InvoiceResponse } from "../../../app/models/api/invoice/invoiceResponse";
import "./Invoices.scss";

interface InvoicesProps {
    data?: InvoiceResponse[];
    isLoading: boolean;
    orderNumber?: string;
}

const Invoices: React.FC<InvoicesProps> = ({ data, isLoading, orderNumber }) => {
    const { t } = useTranslation(namespaces.userAdminPanel);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const handleView = useCallback((invoice: InvoiceTableView) => {
        window.open(`${import.meta.env.VITE_INVOICES}/download/${invoice.id}?inline=true`, "_blank", "noopener");
    }, []);

    const columns = useMemo(
        () =>
            [
                {
                    key: "orderNumber",
                    title: t("invoices.table.orderNumberColumn"),
                    width: "auto",
                    render: (item: string) => {
                        if (!item) return "-";
                        return (
                            <Nav.Link as={Link} className="blue-link" to={`${routes.myOrders.path}/${item}?tab=order`}>
                                {item}
                            </Nav.Link>
                        );
                    },
                },
                {
                    key: "invoiceNumber",
                    title: t("invoices.table.invoiceNumberColumn"),
                    width: "auto",
                },
                {
                    key: "seller",
                    title: t("invoices.table.sellerColumn"),
                    width: "auto",
                },
                {
                    key: "invoiceDate",
                    title: t("invoices.table.invoiceDateColumn"),
                    width: "auto",
                },
                {
                    key: "dueDate",
                    title: t("invoices.table.dueDateColumn"),
                    width: "auto",
                },
                {
                    key: "amount",
                    title: t("invoices.table.amountColumn"),
                    width: "auto",
                },
                {
                    key: "amountPaid",
                    title: t("invoices.table.amountPaidColumn"),
                    width: "auto",
                },
                {
                    key: "status",
                    title: t("invoices.table.statusColumn"),
                    width: "auto",
                    render: (item: InvoiceStatusView) => (
                        <span style={{ color: invoiceStatusColorMapper(item.value) }}>{item.translate}</span>
                    ),
                },
                {
                    key: "actions",
                    title: "",
                    width: "50px",
                    sortable: false,
                    render: (_: unknown, row: InvoiceTableView) => (
                        <Button
                            variant="link"
                            className="p-0 text-primary"
                            onClick={() => handleView(row)}
                            title={t("buttons.view", { ns: namespaces.common })}
                        >
                            <FaEye size={18} />
                        </Button>
                    ),
                },
            ] as ColumnType[],
        [t, handleView]
    );

    const currentTableData = useMemo(() => {
        if (!data) return [];
        const mappedInvoices = data.map(mapInvoiceResponseToTable);
        return searchQuery !== ""
            ? mappedInvoices.filter((item: InvoiceTableView) =>
                  columns.some((column) => {
                      if (column.key === "actions") return false;
                      const value = item[column.key as keyof InvoiceTableView];
                      if (value && typeof value === "object" && "translate" in value) {
                          return value.translate.toLowerCase().includes(searchQuery.toLowerCase());
                      }
                      return value && value.toString().toLowerCase().includes(searchQuery.toLowerCase());
                  })
              )
            : mappedInvoices;
    }, [data, columns, searchQuery]);

    const uploadButton = (
        <Button variant="primary" className="search-button" onClick={() => setShowUploadModal(true)}>
            <FaPlus className="search-button-icon" />
            <span>{t("invoices.buttons.uploadInvoice")}</span>
        </Button>
    );

    return (
        <>
            {showUploadModal && (
                <UploadInvoiceModal
                    isShow={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    orderNumber={orderNumber}
                />
            )}

            <div className="invoices mt-4">
                {isLoading ? (
                    <CustomSpinner />
                ) : (
                    <>
                        <h1 className="mb-4 fw-bold fs-2">{t("invoices.title")}</h1>
                        <Card className="invoices-content">
                            <TableSearchPanel onSearch={(s) => setSearchQuery(s)} headerAction={uploadButton} />
                            <CustomTableWithFrontend
                                data={currentTableData}
                                columns={columns}
                                isSearch={true}
                                isSorting={true}
                                isPagination={true}
                                isLoading={isLoading}
                            />
                        </Card>
                    </>
                )}
            </div>
        </>
    );
};

export default Invoices;
